// Theme system for tender-api  -  Ledger only, two skins (dark / light).
//
// Both skins keep the same product personality: terminal-density type,
// monospace-prominent numerics, signal orange `#ff5f1f` as the only
// accent. The dark skin (default) is the editorial, premium read; the
// light skin is for daylight presentations + procurement leads who
// print landing pages and read them on paper.
//
// Selection precedence: localStorage explicit pick  >  OS prefers-color-
// scheme  >  dark fallback. URL param removed — deep-links shouldn't
// leak the visitor's UI preference into shared analytics.

export const SIGNAL = '#ff5f1f';

export const THEMES = {
  dark: {
    id: 'dark',
    name: 'Dark',
    blurb: 'Terminal black + ivory ink + signal orange.',
    tokens: {
      '--font-sans': "'Inter', system-ui, sans-serif",
      '--font-mono': "'JetBrains Mono', ui-monospace, monospace",
      '--font-display': "'JetBrains Mono', ui-monospace, monospace",

      '--bg-1': '#0a0a0a',
      '--bg-2': '#111113',
      '--bg-3': '#17181b',
      '--fg-0': '#f5f1e8',
      '--fg-1': '#e6e1d4',
      '--fg-2': '#a3a09a',
      '--fg-3': '#6e6c66',
      '--line-1': 'rgba(245, 241, 232, 0.08)',
      '--line-2': 'rgba(245, 241, 232, 0.18)',
      '--accent': SIGNAL,
      '--accent-fg': '#0a0a0a',
      '--accent-soft': 'rgba(255, 95, 31, 0.12)',
      '--success': '#6fcf97',
      '--warn': '#f0c674',
      '--danger': '#e57373',

      '--display-weight': '500',
      '--display-tracking': '-0.01em',
      '--display-leading': '1.0',
      '--surface-radius': '4px',
    },
  },

  light: {
    id: 'light',
    name: 'Light',
    blurb: 'Parchment paper + ink + signal orange.',
    tokens: {
      '--font-sans': "'Inter', system-ui, sans-serif",
      '--font-mono': "'JetBrains Mono', ui-monospace, monospace",
      '--font-display': "'JetBrains Mono', ui-monospace, monospace",

      '--bg-1': '#f5f1e8',
      '--bg-2': '#ece7d8',
      '--bg-3': '#dfd9c6',
      '--fg-0': '#0a0a0a',
      '--fg-1': '#1a1a1a',
      '--fg-2': '#4a4a48',
      '--fg-3': '#7a7872',
      '--line-1': 'rgba(10, 10, 10, 0.10)',
      '--line-2': 'rgba(10, 10, 10, 0.22)',
      '--accent': SIGNAL,
      '--accent-fg': '#f5f1e8',
      '--accent-soft': 'rgba(255, 95, 31, 0.10)',
      '--success': '#2f7a4a',
      '--warn': '#a86a2e',
      '--danger': '#9b2828',

      '--display-weight': '500',
      '--display-tracking': '-0.01em',
      '--display-leading': '1.0',
      '--surface-radius': '4px',
    },
  },
};

export const THEME_IDS = Object.keys(THEMES);
export const DEFAULT_THEME = 'dark';

export function getInitialTheme() {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  // Explicit user pick wins.
  try {
    const stored = window.localStorage.getItem('tender-api:theme');
    if (stored && THEMES[stored]) return stored;
  } catch (_) {
    /* private mode etc. */
  }
  // Otherwise honour the OS preference. First-time visitors get the
  // skin their system already says they want.
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return DEFAULT_THEME;
}

export function applyTheme(id) {
  const t = THEMES[id] || THEMES[DEFAULT_THEME];
  const root = document.documentElement;
  root.setAttribute('data-theme', t.id);
  for (const [k, v] of Object.entries(t.tokens)) {
    root.style.setProperty(k, v);
  }
  // also set color-scheme so form controls + scrollbars adapt
  root.style.colorScheme = t.id === 'light' ? 'light' : 'dark';
  try {
    window.localStorage.setItem('tender-api:theme', t.id);
  } catch (_) {
    /* ignore */
  }
}
